import React, { useEffect } from 'react';
import { array, bool, func, number, object, string } from 'prop-types';
import IconButton from '@alltrails/shared/denali/components/IconButton';
import IconButtonGroup from '@alltrails/shared/denali/components/IconButtonGroup';
import Maps2d from '@alltrails/shared/icons/2d';
import Maps3d from '@alltrails/shared/icons/3d';
import Compass from '@alltrails/shared/icons/Compass';
import Layers from '@alltrails/shared/icons/Layers';
import MapSelectorModal from '../MapSelectorModal';
import { StringConstantsMixin } from '../../application/javascripts/react_components/mixins/shared/string_constants_mixin';
import { styleConfigs } from '../../utils/mapbox/style_configs';
import { overlayConfigs } from '../../utils/mapbox/overlay_configs';
import { configCards } from '../../utils/map_selection_helpers';
import { camelToDash } from '../../utils/camel';
import useToggle from '../../hooks/useToggle';
import '../PrintPage/styles/styles.scss';
import { getMapLocation } from 'utils/amplitudeHelper';
import LanguageSupportUtil from 'utils/language_support_util';
import LanguageRegionCodePropType from 'proptypes/LanguageRegionCodePropType';
import LanguageRegionCode from '@alltrails/shared/types/LanguageRegionCode';
import { logThreeDMapButtonClicked } from '@alltrails/analytics/amplitudeFunctions';
import * as styles from './styles/styles.module.scss';

const getMapTypesCardImage = layerKey => {
  return `https://cdn-assets.alltrails.com/assets/map-layers/v2/map-layer-${layerKey}@2x.png`;
};

const getMapDetailsCardImage = layerKey => {
  return `https://cdn-assets.alltrails.com/assets/map-layers/v2/map-overlay-${camelToDash(layerKey)}@2x.png`;
};

const hotkeysHash = {
  87: 'worldparks', // w
  69: 'openstreet', // e
  84: 'topo', // t
  83: 'alltrailsSatellite', // s
  65: 'arcgisSatellite', // a
  66: 'adminBasemap', // b
  89: 'adminOverlay', // y
  82: 'roadmap', // r
  68: 'worldtopo', // d
  81: 'opencycle', // q
  70: 'alltrailsOutdoorsV2', // f
  80: 'heatmap', // p
  79: 'nearbyTrails', // o
  76: 'recordingsAll', // l
  75: 'recordingsPop' // k
};

const MapSelection = ({
  onCardClick,
  onHeatmapClick,
  currentMapLayer,
  permissions,
  onActivityFilterChange,
  enabledOverlays,
  terrainActive,
  handleResetClick,
  selectedFilterActivity,
  adminCustomizationSettings,
  handleAdminCustomizationSettingsChange,
  mobileBrowser,
  adminLayout,
  currentLayerTerrainEnabled,
  terrainDisabled,
  handleDisable3D,
  handleEnable3D,
  languageRegionCode,
  isAuthenticated,
  isPro,
  handleCompassClick,
  compassRotation
}) => {
  const [open, toggle] = useToggle(false);

  let body;
  let hideForFullscreen;
  if (typeof window !== 'undefined') {
    body = document.querySelector('body');
    hideForFullscreen = document.querySelector('.hide-for-fullscreen-modal');
  }

  const closeModal = () => {
    toggle();
    if (body && hideForFullscreen) {
      body.style.overflow = 'unset';
      hideForFullscreen.style.display = '';
    }
  };

  const openModal = () => {
    toggle();
    if (body && hideForFullscreen) {
      body.style.overflow = 'hidden';
      hideForFullscreen.style.display = 'none';
    }
  };

  const isMapTypeCardSelected = layer => {
    return currentMapLayer === layer;
  };

  const isMapDetailsCardSelected = overlay => {
    return enabledOverlays.includes(overlay);
  };

  const mapTypesKeys = adminLayout
    ? [
        'alltrailsOutdoorsV2',
        'openstreet',
        'alltrailsSatellite',
        'arcgisSatellite',
        'worldparks',
        'roadmap',
        'opencycle',
        'topo',
        'worldtopo',
        'adminBasemap'
      ]
    : ['alltrailsOutdoorsV2', 'roadmap', 'alltrailsSatellite', 'topo', 'worldtopo', 'worldparks', 'openstreet', 'opencycle', 'arcgisSatellite'];

  const mapDetailsKeys = adminLayout
    ? [
        'nearbyTrails',
        'nearbyTrailsHeatmap',
        'heatmap',
        'recordingsAll',
        'recordingsAllHeatmap',
        'recordingsPop',
        'recordingsPopHeatmap',
        'after',
        'limit',
        'pendingTrails',
        'pendingTrailsHeatmap',
        'waypoints',
        'photos',
        'mapsAll',
        'clusterTrails',
        'clusterTrailsHeatmap',
        'contribs',
        'distanceMarkers',
        'adminOverlay'
      ]
    : [
        'waypoints',
        'photos',
        'nearbyTrails',
        'distanceMarkers',
        'heatmap',
        'weather',
        'airQuality',
        'lightPollution',
        'pollen',
        'waymarkedCycling',
        'waymarkedHiking',
        'waymarkedMTB'
      ];

  const mapTypesCards = configCards(mapTypesKeys, styleConfigs, permissions, getMapTypesCardImage, isMapTypeCardSelected, null, 'basemap');

  const mapDetailsCards = configCards(
    mapDetailsKeys,
    overlayConfigs,
    permissions,
    getMapDetailsCardImage,
    isMapDetailsCardSelected,
    terrainActive,
    'overlay'
  );

  let additionalOptionsCards = null;

  if (adminLayout) {
    const additionalOptionsKeys = ['weather', 'airQuality', 'lightPollution', 'pollen', 'waymarkedCycling', 'waymarkedHiking', 'waymarkedMTB'];
    additionalOptionsCards = configCards(
      additionalOptionsKeys,
      overlayConfigs,
      permissions,
      getMapDetailsCardImage,
      isMapDetailsCardSelected,
      null,
      'overlay'
    );
  }

  const renderSelectorModal = () => {
    if (!open) {
      return null;
    }

    return (
      <MapSelectorModal
        mapTypesCards={mapTypesCards}
        mapDetailsCards={mapDetailsCards}
        additionalOptionsCards={additionalOptionsCards}
        handleClose={closeModal}
        handleResetClick={handleResetClick}
        handleCardClick={onCardClick}
        handleHeatmapClick={onHeatmapClick}
        handleActivityFilterChange={onActivityFilterChange}
        selectedFilterActivity={selectedFilterActivity}
        adminCustomizationSettings={adminCustomizationSettings}
        handleAdminCustomizationSettingsChange={handleAdminCustomizationSettingsChange}
        adminLayout={adminLayout}
        admin
        mobileBrowser={mobileBrowser}
        enabledOverlays={enabledOverlays}
      />
    );
  };

  const handleKeyDown = e => {
    // holding alt and other keys will enable different map layers
    if (e.altKey) {
      const layerKey = hotkeysHash[e.keyCode];
      if (layerKey) {
        const card = [...mapDetailsCards, ...mapTypesCards].find(({ key }) => key === layerKey);
        if (card) {
          onCardClick(card);
        }
      }
    }
  };

  useEffect(() => {
    if (typeof window !== 'undefined') {
      document.addEventListener('keydown', handleKeyDown);
      return () => {
        document.removeEventListener('keydown', handleKeyDown);
      };
    }
    return null;
  }, []);

  const getTerrainButton = () => {
    if (terrainDisabled || !currentLayerTerrainEnabled || !handleDisable3D || !handleEnable3D) {
      return null;
    }
    const onClick = () => {
      // Update trail_id with map refactor
      logThreeDMapButtonClicked({ map_location: getMapLocation(window.location.pathname), trail_id: -1 });

      if (terrainActive) {
        handleDisable3D();
      } else if (isPro) {
        handleEnable3D();
      } else {
        const path = `/pro${isAuthenticated ? '' : '/signup'}`;
        window.location = LanguageSupportUtil.wrapUrlSafe(path, languageRegionCode);
      }
    };
    const title = StringConstantsMixin.strings.SEARCH[terrainActive ? 'DISABLE_3D' : 'ENABLE_3D'];
    return { icon: { Component: terrainActive ? Maps2d : Maps3d }, onClick, title, testId: title };
  };

  return (
    <div className="mapLayerControl">
      <IconButtonGroup
        className={terrainActive ? '' : styles.brandColor3DButton}
        buttons={[
          { icon: { Component: Layers }, onClick: openModal, title: StringConstantsMixin.strings.MAP_OPTIONS, testId: 'map-options' },
          getTerrainButton()
        ].filter(button => !!button)}
        orientation="vertical"
        testId="map-selection-controls"
      />
      {terrainDisabled ? null : (
        <IconButton
          className={styles.compass}
          icon={{ Component: Compass, orientation: compassRotation }}
          onClick={handleCompassClick}
          testId="map-compass"
          title={StringConstantsMixin.strings.SEARCH.RESET_NORTH_PITCH}
          variant="elevated"
        />
      )}
      {renderSelectorModal()}
    </div>
  );
};

MapSelection.propTypes = {
  onCardClick: func.isRequired,
  onHeatmapClick: func.isRequired,
  currentMapLayer: string.isRequired,
  permissions: array.isRequired,
  onActivityFilterChange: func.isRequired,
  enabledOverlays: array.isRequired,
  adminCustomizationSettings: object,
  handleAdminCustomizationSettingsChange: func,
  terrainActive: bool,
  adminLayout: bool,
  mobileBrowser: bool,
  selectedFilterActivity: string,
  currentLayerTerrainEnabled: bool,
  terrainDisabled: bool,
  handleDisable3D: func,
  handleEnable3D: func,
  languageRegionCode: LanguageRegionCodePropType,
  isAuthenticated: bool,
  isPro: bool,
  handleCompassClick: func,
  compassRotation: number
};

MapSelection.defaultProps = {
  selectedFilterActivity: null,
  adminCustomizationSettings: {},
  adminLayout: false,
  mobileBrowser: false,
  terrainActive: false,
  handleAdminCustomizationSettingsChange: () => {},
  languageRegionCode: LanguageRegionCode.English,
  currentLayerTerrainEnabled: false,
  terrainDisabled: false,
  isAuthenticated: false,
  isPro: false,
  compassRotation: 0
};

export default MapSelection;
